import React, { useCallback, useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLogin, useTokens } from '@pkg/hooks';
import { TwoFactorAuthType } from '@pkg/types';

import { type Token } from '@app/client/common/structs';
import { callGenericProviderSignIn, callTwitterProviderSignIn } from '@app/client/resources/w3auth';
import { AuthProviders } from '@app/client/resources/w3auth/types';

import { AuthNewLocalStorage } from './Web3Auth.types';

enum AuthSearchParams {
    OAuthToken = 'oauth_token',
    OAuthVerifier = 'oauth_verifier',
    Code = 'code',
    State = 'state',
    Scope = 'scope',
    AuthUser = 'authuser',
    Hd = 'hd',
    Prompt = 'prompt',
}

export interface Web3AuthCallbackProps {
    provider: AuthProviders;
}

export function Web3AuthCallback({ provider }: Web3AuthCallbackProps) {
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const { loginWithToken } = useLogin();
    const { setTokens } = useTokens();

    const oAuthToken = searchParams.get(AuthSearchParams.OAuthToken);
    const oAuthVerifier = searchParams.get(AuthSearchParams.OAuthVerifier);
    const code = searchParams.get(AuthSearchParams.Code);
    const state = searchParams.get(AuthSearchParams.State);

    const handleAuthToken = useCallback(
        (token: Token) => {
            const twoFAShouldPrompt =
                (token?.requires_2fa && token?.requires_2fa !== TwoFactorAuthType.Invalid) || false;
            localStorage.setItem(AuthNewLocalStorage.OAuthLoggedIn, '1');

            if (twoFAShouldPrompt) {
                setTokens(token);
            } else {
                loginWithToken(token);
            }
        },
        [loginWithToken, setTokens],
    );

    useEffect(() => {
        (async () => {
            let token;

            if (provider === AuthProviders.Twitter) {
                if (!oAuthToken || !oAuthVerifier) {
                    navigate('/auth/login');
                    return;
                }

                token = await callTwitterProviderSignIn({ oAuthToken, oAuthVerifier });
            } else {
                if (!code || !state) {
                    navigate('/auth/login');
                    return;
                }

                token = await callGenericProviderSignIn(provider, { code, state });
            }
            handleAuthToken(token);
        })();
    }, [code, handleAuthToken, navigate, oAuthToken, oAuthVerifier, provider, state]);

    return <div />;
}
